import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { TrendingUp, TrendingDown, Zap, Activity, Star, Search, ArrowUpDown, ChevronDown, ChevronRight } from 'lucide-react';
import { getTheme } from '../App';
import { useWS } from '../WSContext';
import ManualOrderModal from '../components/ManualOrderModal';
import NewStrategyModal from '../components/NewStrategyModal';
import ApiKeyPanel from '../components/ApiKeyPanel';

interface Props { dark?: boolean; }

// Başlıktaki açma/kapama düğmeleri. Anahtarlar aşağıdaki bölümlerle eşleşiyor.
const CARD_ROWS = [
  { k: 'hesap',  label: 'Hesap',  title: 'Equity, kullanılabilir bakiye, marjin' },
  { k: 'api',    label: 'API',    title: 'API kimliklerinin geçerlilik süresi' },
  { k: 'piyasa', label: 'Piyasa', title: 'Open interest, funding, spread' },
  { k: 'dolum',  label: 'Dolum',  title: 'Günlük emir dolum oranı' },
] as const;

type SortKey = 'volume' | 'alpha' | 'change' | 'funding' | 'oi';
type SortDir  = 'asc'    | 'desc';

/** Orderly: PERP_HYPE_USDC → HYPE   Bluefin: HYPE-PERP → HYPE */
function displayName(sym: string | undefined): string {
  if (!sym) return '';
  return sym.replace('PERP_', '').replace('_USDC', '').replace('-PERP', '');
}

export default function Dashboard({ dark = true }: Props) {
  const { tickers: rawTickers, account, orderStats, connected, lastUpdate, sendWs,
          favorites: botFavorites, activeBotId } = useWS();
  const tickers = rawTickers as any[];
  const t = getTheme(dark);

  // ── Favoriler — aktif bota özgü ────────────────────────────────────────────
  const [favorites, setFavs] = useState<string[]>([]);

  // Aktif bot değişince veya server'dan favoriler gelince güncelle
  useEffect(() => {
    setFavs(botFavorites || []);
  }, [activeBotId, botFavorites]);

  const toggleFav = useCallback((symbol: string) => {
    setFavs(prev => {
      const next = prev.includes(symbol) ? prev.filter(s => s !== symbol) : [...prev, symbol];
      sendWs({ type: 'update_favorites', payload: next });
      return next;
    });
  }, [sendWs]);

  // ── Manuel emir modal ─────────────────────────────────────────────────────
  const [orderModal, setOrderModal] = useState<{ symbol: string; leverage: number; quantity?: number; side?: 'long' | 'short' } | null>(null);

  // ── Hızlı strateji modalı ─────────────────────────────────────────────────
  const [strategySymbol, setStrategySymbol] = useState<string | null>(null);

  // ── Canlı saat — funding countdown için ──────────────────────────────────
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // Her kart satırı AYRI açılıp kapanır; hepsi varsayılan KAPALI çünkü sayfanın
  // asıl işi fiyat tablosu. Tercih bilerek kalıcı değil — her açılışta yine kapalı.
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const toggleRow = useCallback((k: string) => setOpenRows(o => ({ ...o, [k]: !o[k] })), []);

  // ── Filtreler & sıralama ───────────────────────────────────────────────────
  const [search,       setSearch]       = useState('');
  const [showFavsOnly, setShowFavsOnly] = useState(false);
  const [sortKey,      setSortKey]      = useState<SortKey>('volume');
  const [sortDir,      setSortDir]      = useState<SortDir>('desc');

  const cycleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const sorted = useMemo(() => {
    const base = tickers.filter(tk => {
      if (showFavsOnly && !favorites.includes(tk.symbol)) return false;
      if (search && !displayName(tk.symbol).toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    }).sort((a, b) => {
      if (sortKey === 'alpha')   return sortDir === 'asc' ? a.symbol.localeCompare(b.symbol) : b.symbol.localeCompare(a.symbol);
      const va = sortKey === 'volume' ? (a.volume_24h   || 0)
               : sortKey === 'change' ? (a.price_change_24h || 0)
               : sortKey === 'funding'? (a.est_funding  || 0)
               : (a.open_interest || 0);
      const vb = sortKey === 'volume' ? (b.volume_24h   || 0)
               : sortKey === 'change' ? (b.price_change_24h || 0)
               : sortKey === 'funding'? (b.est_funding  || 0)
               : (b.open_interest || 0);
      return sortDir === 'desc' ? vb - va : va - vb;
    });
    // Favoriler her zaman üstte
    return [
      ...base.filter(tk => favorites.includes(tk.symbol)),
      ...base.filter(tk => !favorites.includes(tk.symbol)),
    ];
  }, [tickers, favorites, search, showFavsOnly, sortKey, sortDir]);

  // ── Stat hesapları ─────────────────────────────────────────────────────────
  const totalOI    = tickers.reduce((s, tk) => s + (tk.open_interest || 0), 0);
  const avgFunding = tickers.length > 0 ? tickers.reduce((s, tk) => s + (tk.est_funding || 0), 0) / tickers.length : 0;
  const maxSpread  = tickers.length > 0 ? Math.max(...tickers.map(tk => tk.spread_pct || 0)) : 0;
  const fillRate   = orderStats.sent > 0 ? (orderStats.filled / orderStats.sent * 100) : 0;

  const card   = `${t.card} border ${t.cardBorder} rounded-xl`;
  const inp    = `border rounded-lg px-2 py-1.5 text-xs focus:outline-none ${t.inp}`;

  const SortBtn = ({ k, label }: { k: SortKey; label: string }) => (
    <button onClick={() => cycleSort(k)}
      className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
        sortKey === k ? 'bg-[#4A9FE8] text-[#061530] font-bold' : `${t.btnSecondary} opacity-70`
      }`}>
      {label}
      {sortKey === k && <ArrowUpDown size={10} />}
    </button>
  );

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* Sabit üst bölüm */}
      <div className="px-6 pt-6 pb-0 space-y-5 shrink-0">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className={`text-xl font-bold ${t.text}`}>Dashboard</h2>
          {/* Her satır için ayrı anahtar. Kapalıyken fiyat tablosu (flex-1)
              boşalan alanı kendiliğinden dolduruyor. */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {CARD_ROWS.map(r => (
              <button key={r.k} onClick={() => toggleRow(r.k)} title={r.title}
                className={`flex items-center gap-1 px-2 py-1 rounded-lg border text-[11px] transition-colors ${
                  openRows[r.k]
                    ? 'border-[#4A9FE8]/60 text-[#4A9FE8]'
                    : `${t.cardBorder} ${t.muted} hover:text-blue-300`}`}>
                {openRows[r.k] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <div className={`flex items-center gap-2 text-xs ${t.muted}`}>
          <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-rose-400'}`} />
          {connected ? `Son güncelleme: ${lastUpdate}` : 'Backend bekleniyor...'}
        </div>
      </div>

      {/* Hesap kartları */}
      {openRows.hesap && account && (
        <div className="grid grid-cols-6 gap-3">
          {[
            { label: 'Equity',           value: `$${account.totalAccountValue?.toFixed(2) ?? '—'}` },
            { label: 'Kullanılabilir',   value: `$${account.availableBalance?.toFixed(2) ?? '—'}` },
            { label: 'USDC Bakiye',      value: `$${account.usdc_balance?.toFixed(2) ?? '—'}` },
            { label: 'Unrealized PnL',   value: `${(account.crossUnrealizedPnl ?? 0) >= 0 ? '+' : ''}$${account.crossUnrealizedPnl?.toFixed(4) ?? '—'}`, pos: (account.crossUnrealizedPnl ?? 0) >= 0 },
            { label: 'Başlangıç Marjin', value: `$${account.totalInitialMargin?.toFixed(4) ?? '—'}` },
            { label: 'Bakım Marjini',    value: `$${account.totalMaintenanceMargin?.toFixed(4) ?? '—'}` },
          ].map(c => (
            <div key={c.label} className={`${card} p-3`}>
              <p className={`text-xs mb-1 ${t.muted}`}>{c.label}</p>
              <p className={`text-sm font-bold ${(c as any).pos !== undefined ? ((c as any).pos ? t.positive : t.negative) : t.text}`}>{c.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* API kimlik geçerliliği — süreli anahtarlar sessizce dolmasın */}
      {openRows.api && <ApiKeyPanel dark={dark} />}

      {/* Piyasa stat kartları */}
      {openRows.piyasa && (
      <div className="grid grid-cols-4 gap-4">
        {[
          { title: 'Toplam Open Interest', value: `$${(totalOI/1e6).toFixed(2)}M`,   icon: <Activity size={14}/> },
          { title: 'Ort. Tahmini Funding',  value: `${avgFunding.toFixed(6)}%`,       pos: avgFunding >= 0, icon: <TrendingUp size={14}/> },
          { title: 'En Yüksek Spread',      value: `${maxSpread.toFixed(4)}%`,        pos: maxSpread < 1,   icon: <TrendingDown size={14}/> },
          { title: 'Enstrüman Sayısı',      value: `${tickers.length}`,               icon: <Zap size={14}/> },
        ].map(c => (
          <div key={c.title} className={`${card} p-4`}>
            <div className="flex items-center gap-1.5 mb-1">
              <span className={t.muted}>{c.icon}</span>
              <p className={`text-xs ${t.muted}`}>{c.title}</p>
            </div>
            <p className={`text-xl font-bold ${(c as any).pos === undefined ? t.text : (c as any).pos ? t.positive : t.negative}`}>{c.value}</p>
          </div>
        ))}
      </div>
      )}

      {/* Emir dolum oranı */}
      {openRows.dolum && (
      <div className={`${card} p-5`}>
        <div className="flex items-center justify-between mb-3">
          <p className={`text-xs font-semibold ${t.muted}`}>Günlük Emir Dolum Oranı</p>
        </div>
        <div className="flex items-end gap-4">
          <div>
            <p className={`text-3xl font-black ${t.text}`}>{orderStats.filled} / {orderStats.sent}</p>
            <p className={`text-sm mt-1 font-mono ${fillRate >= 50 ? t.positive : t.negative}`}>{fillRate.toFixed(1)}% dolum</p>
          </div>
          <div className="flex-1">
            <div className={`w-full h-3 rounded-full ${dark ? 'bg-[#0a2560]' : 'bg-blue-100'}`}>
              <div className="h-3 rounded-full transition-all" style={{ width: `${Math.min(fillRate, 100)}%`, background: '#4A9FE8' }} />
            </div>
            <p className={`text-xs mt-1 ${t.muted}`}>Bugün gönderilen / gerçekleşen</p>
          </div>
        </div>
      </div>
      )}

      </div>{/* /sabit üst bölüm */}

      {/* Piyasa tablosu — kalan alanı doldurur, kendi içinde kaydırılır */}
      <div className="flex-1 min-h-0 px-6 pb-6 flex flex-col">
      <div className={`${card} overflow-hidden flex flex-col flex-1 min-h-0`}>
        <div className={`px-4 py-3 border-b ${t.cardBorder} flex items-center justify-between flex-wrap gap-2`}>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Arama */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${dark ? 'bg-[#0a2560]/60 border-[#003087]' : 'bg-white border-blue-300'}`}>
              <Search size={13} className={t.muted} />
              <input
                placeholder="Sembol ara..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className={`bg-transparent text-xs outline-none w-24 ${t.text}`}
              />
            </div>

            {/* Favoriler */}
            <button onClick={() => setShowFavsOnly(v => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                showFavsOnly ? 'bg-[#4A9FE8] text-[#061530] font-bold' : t.btnSecondary
              }`}>
              <Star size={12} fill={showFavsOnly ? 'currentColor' : 'none'} />
              Favoriler {favorites.length > 0 && `(${favorites.length})`}
            </button>

            <div className={`h-4 w-px ${dark ? 'bg-[#0a2560]' : 'bg-blue-200'}`} />

            {/* Sıralama */}
            <span className={`text-xs ${t.muted}`}>Sırala:</span>
            <SortBtn k="volume"  label="Hacim" />
            <SortBtn k="alpha"   label="A-Z" />
            <SortBtn k="change"  label="24s %" />
            <SortBtn k="funding" label="Funding" />
            <SortBtn k="oi"      label="OI" />
          </div>

          <span className={`text-xs ${t.muted}`}>{sorted.length} enstrüman</span>
        </div>

        {sorted.length === 0 ? (
          <div className={`p-8 text-center ${t.muted} text-xs`}>
            {connected
              ? (showFavsOnly ? 'Favori enstrüman yok.' : 'Veri bekleniyor...')
              : 'Backend bağlantısı kurulamadı'}
          </div>
        ) : (
          <div className="overflow-auto flex-1 min-h-0">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10">
                <tr className={`${t.tableHeader} border-b`}>
                  <th className="px-3 py-2.5 w-8"></th>
                  <th className="px-3 py-2.5 text-left">Sembol</th>
                  <th className="px-3 py-2.5 text-right">Mark</th>
                  <th className="px-3 py-2.5 text-right">24s %</th>
                  <th className="px-3 py-2.5 text-right">Funding Est.</th>
                  <th className="px-3 py-2.5 text-right">Son Funding</th>
                  <th className="px-3 py-2.5 text-right">Kalan</th>
                  <th className="px-3 py-2.5 text-right">Spread %</th>
                  <th className="px-3 py-2.5 text-right">OI</th>
                  <th className="px-3 py-2.5 text-right">24s Hacim</th>
                  <th className="px-3 py-2.5 text-right">Bid</th>
                  <th className="px-3 py-2.5 text-right">Ask</th>
                  <th className="px-3 py-2.5 text-right">Oracle</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(tk => {
                  const isFav      = favorites.includes(tk.symbol);
                  const lastFund   = tk.last_funding;
                  const hasLastFund = lastFund !== undefined && lastFund !== null;
                  return (
                    <tr key={tk.symbol} className={`border-b ${t.tableRow} ${isFav ? (dark ? 'bg-[#4A9FE8]/5' : 'bg-yellow-50/60') : ''}`}>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => toggleFav(tk.symbol)}
                          className={`transition-colors ${isFav ? 'text-[#4A9FE8]' : `${t.muted} hover:text-[#4A9FE8]`}`}>
                          <Star size={13} fill={isFav ? 'currentColor' : 'none'} />
                        </button>
                      </td>
                      <td className={`px-3 py-2 font-semibold ${t.gold}`}>
                        <button
                          onClick={() => setStrategySymbol(tk.symbol)}
                          className={`hover:underline hover:opacity-80 transition-opacity text-left`}
                          title="Yeni strateji oluştur">
                          {displayName(tk.symbol)}
                        </button>
                        {isFav && <span className="ml-1 text-[10px] text-[#4A9FE8] opacity-60">★</span>}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${t.text}`}>${tk.mark_price?.toFixed(4)}</td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${(tk.price_change_24h || 0) >= 0 ? t.positive : t.negative}`}>
                        {(tk.price_change_24h || 0) >= 0 ? '+' : ''}{tk.price_change_24h?.toFixed(2)}%
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${(tk.est_funding || 0) >= 0 ? t.positive : t.negative}`}>
                        {tk.est_funding?.toFixed(6)}%
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${!hasLastFund ? t.muted : (lastFund >= 0 ? t.positive : t.negative)}`}>
                        {hasLastFund ? `${lastFund >= 0 ? '+' : ''}${lastFund.toFixed(6)}%` : '—'}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${t.muted}`}>
                        {(() => {
                          const ms = tk.next_funding;
                          if (!ms) return '—';
                          const left = Math.max(0, ms - now);
                          const h = Math.floor(left / 3_600_000);
                          const m = Math.floor((left % 3_600_000) / 60_000);
                          return h > 0 ? `${h}s ${m}dk` : `${m}dk`;
                        })()}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${(tk.spread_pct || 0) > 0.5 ? t.negative : t.muted}`}>
                        {tk.spread_pct?.toFixed(4)}%
                      </td>
                      <td className={`px-3 py-2 text-right ${t.muted}`}>${((tk.open_interest || 0)/1e6).toFixed(2)}M</td>
                      <td className={`px-3 py-2 text-right font-semibold ${t.text}`}>${((tk.volume_24h || 0)/1e6).toFixed(2)}M</td>
                      <td className={`px-3 py-2 text-right font-mono ${t.positive} cursor-pointer hover:bg-emerald-900/20 transition-colors rounded`}
                          onClick={() => setOrderModal({ symbol: tk.symbol, leverage: 20, side: 'short', quantity: tk.best_bid_qty })}>
                        ${tk.best_bid?.toFixed(4)}
                        {tk.best_bid_qty != null && (
                          <span className={`block text-[10px] ${t.muted}`}>{tk.best_bid_qty >= 1000 ? `${(tk.best_bid_qty/1000).toFixed(1)}K` : tk.best_bid_qty < 1 ? tk.best_bid_qty.toFixed(2) : tk.best_bid_qty.toFixed(0)}</span>
                        )}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${t.negative} cursor-pointer hover:bg-rose-900/20 transition-colors rounded`}
                          onClick={() => setOrderModal({ symbol: tk.symbol, leverage: 20, side: 'long', quantity: tk.best_ask_qty })}>
                        ${tk.best_ask?.toFixed(4)}
                        {tk.best_ask_qty != null && (
                          <span className={`block text-[10px] ${t.muted}`}>{tk.best_ask_qty >= 1000 ? `${(tk.best_ask_qty/1000).toFixed(1)}K` : tk.best_ask_qty < 1 ? tk.best_ask_qty.toFixed(2) : tk.best_ask_qty.toFixed(0)}</span>
                        )}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono text-blue-400`}>
                        {tk.index_price ? `$${tk.index_price.toFixed(4)}` : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>{/* /card */}
      </div>{/* /flex-1 wrapper */}

      {orderModal && (
        <ManualOrderModal
          dark={dark}
          defaultSymbol={orderModal.symbol}
          defaultLeverage={orderModal.leverage}
          defaultQuantity={orderModal.quantity}
          defaultSide={orderModal.side}
          defaultTaker
          onClose={() => setOrderModal(null)}
        />
      )}

      {strategySymbol && (
        <NewStrategyModal
          dark={dark}
          symbol={strategySymbol}
          onClose={() => setStrategySymbol(null)}
        />
      )}
    </div>
  );
}
